import React from 'react';
import ProductRoute from '@jetshop/core/components/DynamicRoute/ProductRoute';
import CategoryRoute from '@jetshop/core/components/DynamicRoute/CategoryRoute';
import ContentRoute from '@jetshop/core/components/DynamicRoute/ContentRoute';
import Status from '@jetshop/core/components/DynamicRoute/Status';
import { Redirect } from 'react-router';
import { setPageType } from '@jetshop/core/analytics/webVitals';
import { DefaultError } from '@jetshop/ui/ErrorBoundary/Generic';
import { splitRoutePath } from '../../utils/splitRoutePath';

const isRouteType = (state, result, type) => {
  if (result.error) {
    return false;
  }

  const stateType = state?.type;
  const routeType = result?.data?.route?.object?.__typename;
  const emptyRoute = !result.loading && !routeType;

  if (emptyRoute) {
    return false;
  }

  return stateType === type || routeType === type;
};

const isEmptyRoute = result => {
  const errorCode =
    result?.error?.graphQLErrors?.[0]?.extensions?.code ?? '';
  const route = result?.data?.route;

  return (
    errorCode.includes('EmptyRoute') ||
    !route ||
    (typeof window !== 'undefined' && window.__STATUS_CODE__ === '404')
  );
};

const Loading = LoadingPage =>
  LoadingPage ? <LoadingPage /> : <div>Loading...</div>;

// Keep root `/`; strip any trailing slashes from everything else
const stripTrailingSlash = path =>
  !path || path.length <= 1 ? path : path.replace(/\/+$/, '');

const RouteResolver = ({
  productPage,
  categoryPage,
  contentPage,
  notFoundPage: NotFoundPage,
  ErrorPage,
  LoadingPage,
  result,
  pathname,
  state,
  search
}) => {
  setPageType(result?.data?.route?.object?.__typename);

  const cleanPathname = stripTrailingSlash(pathname);

  const routePath = result?.data?.route?.path ?? cleanPathname;
  const { pathname: routePathname, hash: routeHash } =
    splitRoutePath(routePath);
  const cleanRoutePathname = stripTrailingSlash(routePathname);

  if (cleanRoutePathname !== pathname) {
    return (
      <Status code={301}>
        <Redirect
          to={{
            pathname: cleanRoutePathname,
            hash: routeHash,
            state: {
              ...state,
              type: result?.data?.route?.object?.__typename
            },
            search
          }}
        />
      </Status>
    );
  }

  if (isRouteType(state, result, 'Product')) {
    return (
      <ProductRoute
        result={result}
        partialProduct={state?.product}
        component={productPage}
      />
    );
  }

  if (isRouteType(state, result, 'Category')) {
    return <CategoryRoute result={result} component={categoryPage} />;
  }

  if (isRouteType(state, result, 'Page')) {
    return (
      <ContentRoute
        pathname={cleanPathname}
        result={result}
        component={contentPage}
      />
    );
  }

  if (result.loading) {
    return Loading(LoadingPage);
  }

  if (isEmptyRoute(result)) {
    return (
      <Status code={404}>
        <NotFoundPage />
      </Status>
    );
  }

  return (
    <Status code={500} error={result.error}>
      {ErrorPage ? (
        <ErrorPage error={result.error} />
      ) : (
        <DefaultError error={result.error} />
      )}
    </Status>
  );
};

export default RouteResolver;
